// ────────────────────────────────────────────────────────────────────────────
//  Icon.tsx — Monochrome SVG-Icon-Bibliothek (Heroicons-Outline-Style)
//
//  Ersetzt die bunten Unicode-Emojis durch line-art Icons die sich theme-
//  aware (currentColor) anpassen. Glas-Variante: kleines abgerundetes Glas-
//  Plättchen mit Blur-Background — das gibt den „Glass-Emoji"-Look.
//
//  Verwendung:
//    <Icon name="chat" />                           → 24px Stroke-Icon
//    <Icon name="chat" className="w-4 h-4" />       → kleinere Größe
//    <GlassIcon name="chat" size={40} tint="accent" />
//                                                   → 40×40 Glas-Plakette
// ────────────────────────────────────────────────────────────────────────────

import type { SVGProps } from 'react'

export type IconName =
  | 'home' | 'chat' | 'punch' | 'briefcase' | 'building' | 'cart'
  | 'user' | 'lock' | 'bug' | 'doc' | 'wallet' | 'recruit'
  | 'bell' | 'eye' | 'eyeOff' | 'globe' | 'logout' | 'arrowRight'
  | 'arrowLeft' | 'sparkles' | 'sun' | 'moon' | 'system'
  | 'check' | 'x' | 'edit' | 'trash' | 'plus' | 'minus'
  | 'reset' | 'archive' | 'apple' | 'shield' | 'mail'
  | 'play' | 'pause' | 'stop' | 'gear' | 'menu' | 'history'
  | 'paint' | 'language' | 'crown' | 'users' | 'calendar'
  | 'chart' | 'star' | 'heart' | 'info' | 'warn' | 'sparkle'
  | 'pinChevron' | 'paperclip' | 'searchPlus' | 'send'

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name:      IconName
  className?: string
  /// Pixel-Größe; default 24. CSS-Klasse `className` hat Vorrang.
  size?:     number
}

// ── Heroicons-Outline-Style (1.6px stroke, rounded line caps) ──────────────
//   Eigene Pfade — alle in einer 24×24-Viewbox, via currentColor stroked.

export function Icon({ name, className, size = 24, ...rest }: IconProps) {
  // WICHTIG: width/height IMMER setzen — sonst rendert der Browser SVGs ohne
  // explizite Größe als 300×150 (CSS-Default) und sie sprengen das Layout.
  // Tailwind-Klassen wie `w-6 h-6` overriden die SVG-Attribute trotzdem
  // (CSS-Spezifität schlägt SVG-Presentation-Attribute), daher kein Konflikt.
  const common = {
    width:  size,
    height: size,
    viewBox: '0 0 24 24',
    fill:    'none',
    stroke:  'currentColor',
    strokeWidth:    '1.6',
    strokeLinecap:  'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    ...rest,
  }

  switch (name) {
    case 'home':         return <svg {...common}><path d="M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z"/></svg>
    case 'chat':         return <svg {...common}><path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l.8-5.5A8 8 0 1 1 21 12Z"/><path d="M9 11h.01M12 11h.01M15 11h.01"/></svg>
    case 'send':         return <svg {...common}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/></svg>
    case 'punch':        return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
    case 'briefcase':    return <svg {...common}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18"/></svg>
    case 'building':     return <svg {...common}><rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 7h.01M14 7h.01M9 11h.01M14 11h.01M9 15h.01M14 15h.01M10 21v-3h4v3"/></svg>
    case 'cart':         return <svg {...common}><circle cx="9" cy="20" r="1.5"/><circle cx="17" cy="20" r="1.5"/><path d="M3 4h2l2.5 12h11l2-8H7"/></svg>
    case 'user':         return <svg {...common}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>
    case 'users':        return <svg {...common}><circle cx="9" cy="9" r="3.5"/><path d="M3 19a6 6 0 0 1 12 0"/><circle cx="17" cy="7" r="3"/><path d="M16 14a5 5 0 0 1 5 5"/></svg>
    case 'lock':         return <svg {...common}><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
    case 'bug':          return <svg {...common}><path d="M8 4 6 2M16 4l2-2"/><path d="M9 6h6a3 3 0 0 1 3 3v6a6 6 0 1 1-12 0V9a3 3 0 0 1 3-3Z"/><path d="M3 12h3M18 12h3M3 8h2M19 8h2M3 17h2M19 17h2"/></svg>
    case 'doc':          return <svg {...common}><path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M9 13h6M9 17h6"/></svg>
    case 'wallet':       return <svg {...common}><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M3 10h18M16 14h2"/></svg>
    case 'recruit':      return <svg {...common}><path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"/><path d="M2 21a7 7 0 0 1 14 0"/><path d="m20 8-3 3-2-2"/></svg>
    case 'bell':         return <svg {...common}><path d="M18 16V11a6 6 0 0 0-12 0v5l-2 3h16Z"/><path d="M10 19a2 2 0 0 0 4 0"/></svg>
    case 'eye':          return <svg {...common}><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>
    case 'eyeOff':       return <svg {...common}><path d="M2 12s4-7 10-7c2.4 0 4.5 1 6.2 2.4M22 12s-4 7-10 7c-2.4 0-4.5-1-6.2-2.4M3 3l18 18"/></svg>
    case 'globe':        return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>
    case 'logout':       return <svg {...common}><path d="M9 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M16 17l5-5-5-5M21 12H9"/></svg>
    case 'arrowRight':   return <svg {...common}><path d="M5 12h14M13 5l7 7-7 7"/></svg>
    case 'arrowLeft':    return <svg {...common}><path d="M19 12H5M11 5l-7 7 7 7"/></svg>
    case 'sparkles':     return <svg {...common}><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2.1 2.1M16.4 16.4l2.1 2.1M5.5 18.5l2.1-2.1M16.4 7.6l2.1-2.1M12 8a4 4 0 0 0 4 4 4 4 0 0 0-4 4 4 4 0 0 0-4-4 4 4 0 0 0 4-4Z"/></svg>
    case 'sparkle':      return <svg {...common}><path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z"/></svg>
    case 'sun':          return <svg {...common}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
    case 'moon':         return <svg {...common}><path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/></svg>
    case 'system':       return <svg {...common}><rect x="2" y="4" width="20" height="14" rx="2"/><path d="M8 22h8M12 18v4"/></svg>
    case 'check':        return <svg {...common}><path d="m20 6-11 11-5-5"/></svg>
    case 'x':            return <svg {...common}><path d="M18 6 6 18M6 6l12 12"/></svg>
    case 'edit':         return <svg {...common}><path d="M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z"/></svg>
    case 'trash':        return <svg {...common}><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6"/></svg>
    case 'plus':         return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>
    case 'minus':        return <svg {...common}><path d="M5 12h14"/></svg>
    case 'reset':        return <svg {...common}><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>
    case 'archive':      return <svg {...common}><rect x="3" y="3" width="18" height="5" rx="1"/><path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8M10 12h4"/></svg>
    case 'apple':        return <svg {...common}><path d="M16 8.5a3.5 3.5 0 0 0-2.5-1.4 3.6 3.6 0 0 0-2.5 1.4 3.6 3.6 0 0 0-2.5-1.4 4 4 0 0 0-3.5 4 8 8 0 0 0 4 7c1.4.7 3.6.7 5 0a8 8 0 0 0 4-7 4 4 0 0 0-2-2.6Z"/><path d="M12 7c0-1 .5-2.4 1.5-3"/></svg>
    case 'shield':       return <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/></svg>
    case 'crown':        return <svg {...common}><path d="m3 8 3 11h12l3-11-5 4-4-7-4 7z"/></svg>
    case 'mail':         return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 7 9-7"/></svg>
    case 'play':         return <svg {...common}><path d="M6 4v16l14-8z"/></svg>
    case 'pause':        return <svg {...common}><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
    case 'stop':         return <svg {...common}><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
    case 'gear':         return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
    case 'menu':         return <svg {...common}><path d="M3 6h18M3 12h18M3 18h18"/></svg>
    case 'history':      return <svg {...common}><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5M12 7v5l4 2"/></svg>
    case 'paint':        return <svg {...common}><path d="M19 11a7 7 0 1 0-14 0c0 4 4 5 4 7a3 3 0 0 0 3 3 4 4 0 0 0 4-4c0-2 3-2 3-6Z"/><circle cx="6.5" cy="11.5" r=".5" fill="currentColor"/><circle cx="9" cy="6" r=".5" fill="currentColor"/><circle cx="14" cy="5" r=".5" fill="currentColor"/><circle cx="17" cy="9" r=".5" fill="currentColor"/></svg>
    case 'language':     return <svg {...common}><path d="M3 5h12M9 3v2M5 5c0 5 3 8 7 9M9 16l5-9 5 9M11.5 13h5"/></svg>
    case 'calendar':     return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>
    case 'chart':        return <svg {...common}><path d="M3 21V3M3 21h18M7 17v-6M11 17V9M15 17v-4M19 17V7"/></svg>
    case 'star':         return <svg {...common}><path d="m12 3 2.6 5.6 6.4.9-4.6 4.5 1.1 6.3L12 17.4 6.5 20.3l1.1-6.3L3 9.5l6.4-.9z"/></svg>
    case 'heart':        return <svg {...common}><path d="M20.84 4.6a5.5 5.5 0 0 0-7.78 0L12 5.7l-1.06-1.1a5.5 5.5 0 0 0-7.78 7.78L12 21l8.84-8.62a5.5 5.5 0 0 0 0-7.78Z"/></svg>
    case 'info':         return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg>
    case 'warn':         return <svg {...common}><path d="m12 2 11 19H1z"/><path d="M12 9v5M12 17h.01"/></svg>
    case 'pinChevron':   return <svg {...common}><path d="m9 18 6-6-6-6"/></svg>
    case 'paperclip':    return <svg {...common}><path d="m21 12-9.5 9.5a5 5 0 0 1-7-7L14 5a3.5 3.5 0 0 1 5 5l-9.5 9.5a2 2 0 0 1-3-3L15 8"/></svg>
    case 'searchPlus':   return <svg {...common}><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3M8 11h6M11 8v6"/></svg>
  }
}

// ── Glass-Wrapper ──────────────────────────────────────────────────────────
//   Kleines Plättchen mit Glas-Background um das Icon. Tint setzt sowohl
//   Icon-Farbe als auch einen subtilen Tint im Background.

interface GlassIconProps {
  name:    IconName
  size?:   number                   // 32, 40, 48 ...
  tint?:   'accent' | 'teal' | 'coral' | 'brandBlue' | 'amber' | 'neutral'
  className?: string
}

const tints: Record<NonNullable<GlassIconProps['tint']>, { fg: string; bg: string; ring: string }> = {
  accent:    { fg: '#8a44ee', bg: 'rgba(138,68,238,0.10)', ring: 'rgba(138,68,238,0.20)' },
  teal:      { fg: '#33d670', bg: 'rgba(51,214,112,0.10)',  ring: 'rgba(51,214,112,0.22)' },
  coral:     { fg: '#ff5b5c', bg: 'rgba(255,91,92,0.10)',   ring: 'rgba(255,91,92,0.22)' },
  brandBlue: { fg: '#3a62ff', bg: 'rgba(58,98,255,0.10)',   ring: 'rgba(58,98,255,0.22)' },
  amber:     { fg: '#ffb300', bg: 'rgba(255,179,0,0.10)',   ring: 'rgba(255,179,0,0.22)' },
  neutral:   { fg: 'var(--text)',  bg: 'var(--bg-elev)',    ring: 'var(--border-strong)' },
}

export function GlassIcon({ name, size = 40, tint = 'neutral', className = '' }: GlassIconProps) {
  const t = tints[tint]
  const inner = Math.round(size * 0.55)
  return (
    <span
      className={`inline-grid place-items-center rounded-2xl shrink-0 ${className}`}
      style={{
        width:  size,
        height: size,
        background: t.bg,
        border:  `1px solid ${t.ring}`,
        color:   t.fg,
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
      }}
    >
      <Icon name={name} size={inner} />
    </span>
  )
}
